import React from 'react';
import ReactQuill, { Quill, Parchment } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import ImageUploader from 'quill-image-uploader';
import { Stack, Typography } from '@mui/material';
import GeneralLoader from './GeneralLoader';
import { v4 as uuidv4 } from 'uuid';
import { fetchWith401Check } from '../helpers/http';

const Image = Quill.import('formats/image'); // Had to get the class this way, instead of ES6 imports, so that quill could register it without errors

const ATTRIBUTES = [
  'alt',
  'height',
  'width',
  'class',
  'style', // Had to add this line because the style was inlined
];

const API_URL = process.env.REACT_APP_STAGE === 'DEVELOPMENT' ? process.env.REACT_APP_API_HOST : process.env.REACT_APP_API_HOST_PROD;

class CustomImage extends Image {
  static formats(domNode) {
    return ATTRIBUTES.reduce((formats, attribute) => {
      const copy = { ...formats };

      if (domNode.hasAttribute(attribute)) {
        copy[attribute] = domNode.getAttribute(attribute);
      }

      return copy;
    }, {});
  }

  format(name, value) {
    if (ATTRIBUTES.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}

Quill.register('formats/image', CustomImage);
Quill.register('modules/imageResize', ImageResize);
Quill.register('modules/imageUploader', ImageUploader);

const modules = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link', 'image', 'video'],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize', 'Toolbar'],
  },
};

const formats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'align',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'video',
  'width',
  'height',
  'resize',
  'toolbar',
  'class',
  'style',
];

modules.imageUploader = {
  upload: (file) => {
    return new Promise((resolve, reject) => {
      // setIsLoading(true);
      const formData = new FormData();
      let extension = '';

      if (file.type === 'image/jpeg') extension = '.jpg';
      if (file.type === 'image/png') extension = '.png';

      const uuid = uuidv4();

      const newFilename = `${uuid}${extension}`;
      let newFile = new File([file], newFilename, {
        type: file.type,
        lastModified: Date.now(),
      });
      formData.append('upload', newFile);

      fetchWith401Check(`${API_URL}/uploadfile`, {
        method: 'POST',
        body: formData,
      })
        .then((response) => response.json())
        .then((result) => {
          console.log(result);
          resolve(`${API_URL}/download?filename=keshif/${result.originalname}`);
          // setIsLoading(false);
        })
        .catch((error) => {
          // setIsLoading(false);
          reject('Upload failed');
          console.error('Error:', error);
        });
    });
  },
};

const HTMLEditor = ({ state, setState, currentHtml }) => {
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setState({ ...state, value: currentHtml.replaceAll('\\', '') });
  }, []);

  const handleChange = (html) => {
    console.log('change', html);
    setState({ ...state, value: html });
  };

  return (
    <Stack direction='column' alignItems='center'>
      <GeneralLoader isLoading={isLoading} />

      <ReactQuill
        style={{
          height: 250,
        }}
        onChange={handleChange}
        value={state.value}
        modules={modules}
        formats={formats}
        bounds={'#root'}
        placeholder='Enter your text here'
        theme='snow'
      />
    </Stack>
  );
};

export default HTMLEditor;
